import { ReactNode, Suspense, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import {
  ArrowLeft,
  Loader2,
  Menu,
  Moon,
  PanelLeftOpen,
  Search,
  Sun,
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { UserProfileMenu } from "@/components/auth/UserProfileMenu";
import { cn } from "@/lib/utils";
import { ACCOUNT_SCOPED_PATHS, SHELL_FOOTER_NAV, pageTitleFor } from "./navigation";
import { ExpiredPlanBanner } from "./ExpiredPlanBanner";
import { ShellSidebarBody, useShellNavItems } from "./ShellSidebarBody";
import { SidebarWorkspaceSwitch } from "./SidebarWorkspaceSwitch";
import { PageSearch } from "./PageSearch";
import { AttentionBell } from "@/components/notifications/AttentionBell";

const COLLAPSE_KEY = "joylo-sidebar-collapsed";

interface AppShellProps {
  children: ReactNode;
  // Router state carried on every shell link, so settings opened over the editor stay an overlay
  linkState?: unknown;
  // Shown in the topbar when the shell is an overlay over another page
  onBack?: () => void;
  backLabel?: string;
  // Pages that manage their own width/padding (e.g. full-bleed layouts) opt out of the content container
  bare?: boolean;
}

export const AppShell = ({ children, linkState, onBack, backLabel = "Back", bare = false }: AppShellProps) => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { resolvedTheme, setTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const { groups, roleItems } = useShellNavItems();

  const searchItems = useMemo(
    () => [...groups.flatMap((g) => g.items), ...SHELL_FOOTER_NAV, ...roleItems],
    [groups, roleItems],
  );

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      /* storage can be unavailable in private windows */
    }
  }, [collapsed]);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const sidebarHidden = isMobile ? !mobileOpen : collapsed;
  const title = pageTitleFor(location.pathname);
  const accountScoped = ACCOUNT_SCOPED_PATHS.includes(location.pathname);

  return (
    <div className="jl-shell min-h-[100dvh] bg-canvas text-[14px] text-foreground">
      {isMobile && mobileOpen && (
        <button
          className="fixed inset-0 z-[39] bg-black/40"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        id="workspace-navigation"
        aria-label="Workspace navigation"
        // React 18 has no typed inert prop; an empty-string attribute is how it reaches the DOM
        {...(sidebarHidden ? ({ inert: "" } as Record<string, string>) : {})}
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-[234px] flex-col border-r border-line bg-surface px-[15px] pt-5 pb-[15px] transition-transform duration-200",
          sidebarHidden && "-translate-x-full",
          isMobile && "shadow-2xl",
        )}
      >
        <ShellSidebarBody
          linkState={linkState}
          closeVariant={isMobile ? "drawer" : "rail"}
          onClose={() => (isMobile ? setMobileOpen(false) : setCollapsed(true))}
        />
      </aside>

      <div className={cn("min-h-[100dvh] transition-[margin] duration-200", !isMobile && !collapsed && "ml-[234px]")}>
        <header className="sticky top-0 z-30 grid min-h-[68px] grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-5 border-b border-line bg-canvas/95 px-4 py-3 backdrop-blur sm:px-6">
          <div className="flex min-w-0 items-center gap-3.5">
            {/* An open desktop rail carries its own close button, so the topbar only offers the way back in */}
            {sidebarHidden && (
              <button
                className="grid h-9 w-9 place-items-center rounded-lg text-muted-foreground hover:bg-soft hover:text-foreground"
                aria-label={isMobile ? "Open navigation" : "Show navigation"}
                aria-expanded={false}
                aria-controls="workspace-navigation"
                onClick={() => (isMobile ? setMobileOpen(true) : setCollapsed(false))}
              >
                {isMobile ? <Menu className="w-[19px] h-[19px]" /> : <PanelLeftOpen className="w-[19px] h-[19px]" />}
              </button>
            )}
            {/* Sidebar pages get a way home; overlays and pages opened from a project pass their own onBack */}
            {!onBack && location.pathname !== "/~" && (
              <Link
                to="/~"
                state={linkState}
                aria-label="Back to overview"
                title="Back to overview"
                className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] font-medium text-muted-foreground hover:bg-soft hover:text-foreground"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Overview</span>
              </Link>
            )}
            {onBack && (
              <button
                onClick={onBack}
                className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] font-medium text-muted-foreground hover:bg-soft hover:text-foreground"
              >
                <ArrowLeft className="w-4 h-4" /> {backLabel}
              </button>
            )}
            {!onBack && !isMobile &&
              (accountScoped ? (
                <span className="text-[13px] text-muted-foreground">Your account</span>
              ) : (
                <SidebarWorkspaceSwitch variant="crumb" linkState={linkState} />
              ))}
          </div>
          {/* Overview is home, so a title there is redundant; the empty cell keeps the 3-column grid aligned */}
          {location.pathname === "/~" ? (
            <div />
          ) : (
            <div className="min-w-0 border-l border-input pl-5">
              <strong className="block truncate text-[13px] font-medium text-foreground">{title}</strong>
            </div>
          )}
          <div className="flex items-center gap-1.5 sm:gap-2.5">
            <button
              className="hidden items-center gap-2.5 rounded-lg px-2 py-2 text-xs text-muted-foreground hover:text-foreground sm:flex"
              aria-label="Search pages"
              onClick={() => setSearchOpen(true)}
            >
              <Search className="w-[17px] h-[17px]" />
              <span>Search</span>
              <kbd className="rounded border border-input px-1 font-sans text-[10px]">⌘ K</kbd>
            </button>
            <button
              className="grid h-9 w-9 place-items-center rounded-lg text-muted-foreground hover:bg-soft hover:text-foreground"
              aria-label={resolvedTheme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
            >
              {resolvedTheme === "dark" ? <Sun className="w-[17px] h-[17px]" /> : <Moon className="w-[17px] h-[17px]" />}
            </button>
            <AttentionBell />
            <UserProfileMenu hideName />
          </div>
        </header>

        <ExpiredPlanBanner linkState={linkState} />

        <main id="main-content" tabIndex={-1} className={cn(!bare && "mx-auto max-w-[1420px] px-4 py-7 sm:px-10 sm:py-9")}>
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-24 text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            }
          >
            {children}
          </Suspense>
        </main>
      </div>

      <PageSearch open={searchOpen} onOpenChange={setSearchOpen} items={searchItems} linkState={linkState} />
    </div>
  );
};

// Routes rendered inside the shell; the floating theme toggle hides here since the topbar has its own
export const SHELL_PATHS = [
  "/~",
  "/projects/all",
  "/connectors",
  "/expert",
  "/profile",
  "/universal-ai-key",
  "/referrals",
  "/billing/manage",
  "/published",
  "/my-tickets",
  "/influencer",
  "/workspace/settings",
  "/analytics",
];

export const isShellPath = (pathname: string) =>
  SHELL_PATHS.includes(pathname) || pathname === "/admin" || pathname.startsWith("/admin/") || pathname.startsWith("/billing/") || pathname.startsWith("/expert/") || /^\/projects\/[^/]+\/[^/]+\/engagement\//.test(pathname);

export const useIsShellRoute = () => {
  const { pathname } = useLocation();
  return isShellPath(pathname);
};

export default AppShell;
